import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faEye } from "@fortawesome/pro-solid-svg-icons";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";

import { Conversation } from "@/pages/Private/pages/Apollo/schema/get-people";

import { RecentSignalRow } from "./RecentSignalRow";
import { RecentActivityRow } from "./RecentActivityRow";
import { AddToTrackerButton } from "./AddToTrackerButton";
import { useLatestProspectSignal } from "./useLatestProspectSignal";
import { pickLatestOutboundActivity, toActivityView, toSignalView } from "./recentSignalsBox.utils";

interface RecentSignalsBoxProps {
	prospectId?: number;
	prospectName: string;
	firstName: string;
	companyName?: string;
	/** Whether the lead/company is on the Profile Tracker — gates signals and the empty-state copy. */
	isTracked: boolean;
	/** The lead's conversations (sent email / LinkedIn) — the source of the latest-activity row. */
	conversations?: Conversation[] | null;
	/** Switch the host panel to its Timeline tab (the "See all in Timeline →" footer). */
	onSeeTimeline: () => void;
	portalContainer?: HTMLElement | null;
}

/**
 * "Recent Signals & Activities" Overview box (mockup `lp-recent-box`). Renders the latest signal
 * (only when tracked) and the latest outbound activity in one boxed section, with a "See all in
 * Timeline" footer. Empty states: not tracked → prompt + "Add to Profile Tracker"; tracked but
 * quiet → "Watching for signals".
 */
export const RecentSignalsBox: FunctionComponent<RecentSignalsBoxProps> = ({
	prospectId,
	prospectName,
	firstName,
	companyName,
	isTracked,
	conversations,
	onSeeTimeline,
	portalContainer,
}) => {
	const { t } = useTranslation();

	const { signal } = useLatestProspectSignal(prospectId, isTracked);
	const signalView = signal ? toSignalView(signal) : null;
	const hasSignal = isTracked && !!signalView?.title;

	const activityConversation = pickLatestOutboundActivity(conversations);
	const activityView = activityConversation ? toActivityView(activityConversation) : null;

	// The footer only makes sense when there is real timeline content to "see all" of.
	const showFooter = hasSignal || !!activityView;

	const renderPrompt = () => {
		if (isTracked) {
			return (
				<div className="flex items-start gap-2.5 p-3" data-testid="lead-panel-recent-watching">
					<span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-background-success text-text-success">
						<Icon className="w-3.5" icon={faEye} />
					</span>
					<div className="min-w-0 flex-1">
						<p className="text-[13px] font-semibold text-text">
							{t("contacts.leadPanel.recent.watching.title")}
						</p>
						<p className="mt-0.5 text-xs leading-relaxed text-text-secondary">
							{t("contacts.leadPanel.recent.watching.subtitle")}
						</p>
					</div>
				</div>
			);
		}

		return (
			<div className="flex items-start gap-2.5 p-3" data-testid="lead-panel-recent-prompt">
				<span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-blue-action-200 text-brand-light">
					<Icon className="w-3.5" icon={faEye} />
				</span>
				<div className="flex min-w-0 flex-1 flex-col items-start gap-2.5">
					<div>
						<p className="text-[13px] font-semibold text-text">
							{t("contacts.leadPanel.recent.empty.title")}
						</p>
						<p className="mt-0.5 text-xs leading-relaxed text-text-secondary">
							{t("contacts.leadPanel.recent.empty.subtitle", {
								name: firstName,
								company: companyName ?? t("contacts.leadPanel.company"),
							})}
						</p>
					</div>
					{prospectId && (
						<AddToTrackerButton
							portalContainer={portalContainer}
							prospectId={prospectId}
							prospectName={prospectName}
						/>
					)}
				</div>
			</div>
		);
	};

	return (
		<section className="flex flex-col gap-2" data-testid="lead-panel-recent">
			<h3 className="text-sm font-semibold text-text">{t("contacts.leadPanel.recent.title")}</h3>

			<div
				className={classNames(
					"overflow-hidden rounded-lg border border-border-light bg-white",
					"divide-y divide-border-light"
				)}
			>
				{hasSignal && signalView && <RecentSignalRow view={signalView} />}
				{activityView && <RecentActivityRow firstName={firstName} view={activityView} />}
				{!hasSignal && renderPrompt()}

				{showFooter && (
					<button
						className="flex w-full cursor-pointer items-center bg-brand-white px-3 py-2.5 text-left text-xs font-semibold text-brand-light duration-150 hover:bg-blue-action-200"
						data-testid="lead-panel-recent-see-all"
						type="button"
						onClick={onSeeTimeline}
					>
						{t("contacts.leadPanel.recent.seeAll")}
					</button>
				)}
			</div>
		</section>
	);
};
